import type { CSSProperties, HTMLAttributes } from "react";
import "./Skeleton.css";

export type SkeletonRadius = "sm" | "md" | "lg" | "full";

export interface SkeletonProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** Numbers are treated as px; strings pass through (`"100%"`, `"12ch"`). */
  width?: number | string;
  height?: number | string;
  /**
   * Match the radius of the element being stood in for: one of the four token
   * names, or any CSS length — including a token reference such as
   * `"var(--fc-tile-radius)"` for a component with its own radius variable.
   */
  radius?: SkeletonRadius | (string & {});
}

const RADIUS_KEYS: readonly SkeletonRadius[] = ["sm", "md", "lg", "full"];

function isRadiusToken(value: string): value is SkeletonRadius {
  return (RADIUS_KEYS as readonly string[]).includes(value);
}

function toLength(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === "number" ? `${String(value)}px` : value;
}

/**
 * A placeholder at the exact geometry of the content it replaces. Hidden from
 * assistive tech — announce loading on the container with `aria-busy` instead.
 */
export function Skeleton({
  width,
  height,
  radius = "sm",
  className,
  style,
  ...rest
}: SkeletonProps) {
  const token = isRadiusToken(radius);

  const classes = [
    "fc-skeleton",
    token ? `fc-skeleton--r-${radius}` : null,
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  const merged: CSSProperties = {
    width: toLength(width),
    height: toLength(height),
    borderRadius: token ? undefined : radius,
    ...style,
  };

  return (
    <div {...rest} className={classes} style={merged} aria-hidden="true" />
  );
}
